import React, {useEffect, useRef, useState} from "react";
import Dialog from "../../components/Dialog/Dialog";
import {withStyles} from "@material-ui/core";
import CloseIcon from "../../components/Icon/Close";
import Table from "../../components/Table/Table";
import Notification from "../../components/Notification";
import InfiniteScroll from "react-infinite-scroller";
import {buscarResultadoExame, findAllExamesPacsweb} from "../../services/ExamePacswebService";
import {gerarUrlViewerExame} from "../../services/SolicitacoesPacientesService";
import {columns} from "../SujeitoAtencao/ExamesPacsweb/Constants";
import ModalViewerExame from "../SujeitoAtencao/ExamesPacsweb/ModalViewerExame";
import InputSearch from "../../components/Input/Input";

const ModalExamesPacsweb = ({ classes, open, onClose, sujeitoAtencaoId, profissionalSaudeId }) => {
  const [search, setSearch] = useState("");
  const [exames, setExames] = useState([]);
  const [last, setLast] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [ordenacao, setOrdenacao] = useState({ sortField: "atendimentoDataHora", sortDir: "DESC" });
  const [viewerModal, setViewerModal] = useState({ open: false, url: "", loading: false });
  const [loadingBuscarResultado, setLoadingBuscarResultado] = useState(null);
  const [notification, setNotification] = useState({ isOpen: false, message: "", variant: "" });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && sujeitoAtencaoId) {
      findAllExames(true);
    }
    if (!open) {
      setExames([]);
      setPageNumber(0);
      setLast(false);
      setSearch("");
    }
  }, [open, ordenacao]);

  const findAllExames = async (resetValues) => {
    try {
      if (isLoading) return;
      setIsLoading(true);
      const { content, last: isLast } = await findAllExamesPacsweb({
        sujeitoAtencaoId,
        profissionalSaudeId,
        search,
        pageableDto: {
          pageNumber: resetValues ? 0 : pageNumber,
          pageSize: 30,
          sortField: ordenacao.sortField,
          sortDir: ordenacao.sortDir,
        },
      });
      setExames((prev) => (resetValues ? content : [...prev, ...content]));
      setLast(isLast);
      setPageNumber(resetValues ? 1 : pageNumber + 1);
    } catch (error) {
      showAlert("Erro ao carregar exames!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickOrdenar = (field) => {
    const sortDir = ordenacao.sortField !== field ? "ASC" : ordenacao.sortDir === "ASC" ? "DESC" : "ASC";
    setOrdenacao({ sortField: field, sortDir });
  };

  const handleVisualizarImagem = async (exame) => {
    const pacienteAnexoId = exame?.pacienteAnexo?.id;
    if (!pacienteAnexoId) {
      showAlert("Exame sem imagem vinculada.", "error");
      return;
    }
    setViewerModal({ open: true, url: "", loading: true });
    try {
      const url = await gerarUrlViewerExame(pacienteAnexoId);
      if (url && url.startsWith("http")) {
        setViewerModal({ open: true, url, loading: false });
      } else {
        setViewerModal({ open: false, url: "", loading: false });
        showAlert(url || "Não foi possível gerar a URL do viewer.", "error");
      }
    } catch (error) {
      setViewerModal({ open: false, url: "", loading: false });
      showAlert(error.message, "error");
    }
  };

  const handleSearch = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    findAllExames(true);
  };

  const showAlert = (message, variant) => {
    setNotification({ isOpen: true, message, variant });
    setTimeout(() => setNotification({ isOpen: false, message: "", variant: "" }), 3000);
  };

  const handleBuscarResultado = async (exame) => {
    if (loadingBuscarResultado) return;
    setLoadingBuscarResultado(exame.id);
    try {
      await buscarResultadoExame(exame.id);
      showAlert("Resultado buscado com sucesso!", "success");
      findAllExames(true);
    } catch (error) {
      showAlert(error.message, "error");
    } finally {
      setLoadingBuscarResultado(null);
    }
  };

  const handleLoadMore = () => {
    if (!isLoading && !last) {
      findAllExames(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
        <div className={classes.root}>
          <div className={classes.header}>
            <div className={classes.title}>Exames do Paciente</div>
            <div onClick={onClose} className={classes.close}>
              <CloseIcon />
            </div>
          </div>
          <div className={classes.filterContainer}>
            <form onSubmit={handleSearch} className={classes.form}>
              <InputSearch
                placeholder="Pesquisar exame"
                onChange={(e) => setSearch(e.target.value)}
                value={search}
                onClickSearch={handleSearch}
              />
            </form>
          </div>
          <div className={classes.content} ref={scrollRef}>
            <InfiniteScroll
              loadMore={handleLoadMore}
              hasMore={!last && !isLoading}
              useWindow={false}
              getScrollParent={() => scrollRef.current}
              threshold={100}
            >
              <Table
                dados={exames}
                columns={columns({ handleVisualizarImagem, handleBuscarResultado, loadingBuscarResultado })}
                emptyMessage={"Nenhum exame encontrado"}
                isLoading={isLoading}
                comOrdenacao
                handleClickOrdenar={handleClickOrdenar}
                ordenarTabela={ordenacao}
              />
            </InfiniteScroll>
          </div>
        </div>
        <Notification
          close={() => setNotification({ isOpen: false, message: "", variant: "" })}
          reset={() => setNotification({ isOpen: false, message: "", variant: "" })}
          isOpen={notification.isOpen}
          variant={notification.variant}
          message={notification.message}
        />
      </Dialog>
      <ModalViewerExame
        open={viewerModal.open}
        onClose={() => setViewerModal({ open: false, url: "", loading: false })}
        url={viewerModal.url}
        loading={viewerModal.loading}
      />
    </>
  );
};

const styles = {
  paper: {
    width: "100%",
    maxWidth: "900px",
    height: "80vh",
    maxHeight: "80vh",
    borderRadius: "10px",
    overflow: "hidden",
  },
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
    background: "#fff",
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#219A97",
  },
  close: {
    cursor: "pointer",
  },
  filterContainer: {
    padding: "12px 24px",
    background: "#fff",
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
  },
  form: {
    width: "100%",
  },
  content: {
    flex: 1,
    overflowY: "auto",
    background: "#F2F2F2",
  },
};

export default withStyles(styles)(ModalExamesPacsweb);
